import { useState, useEffect, useRef } from 'react'
import { Text } from '@consta/uikit/Text'
import { TextField } from '@consta/uikit/TextField'
import { Button } from '@consta/uikit/Button';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLoginMutation } from '../../services/authService';
import { PostLoginRequest, PostSendCodeRequest } from '../../services/types/TLoginServices';
import { RootState } from '../../store/store';
import { setTokens, setUserInfo } from '../../store/authSlice';
import classes from './Login.module.scss'
import { API_URL } from '../../store/constants';
import { useLazyUserInfoQuery } from '../../services/userService';

const Login = () => {
    const [email, setEmail] = useState<any>('')
    const [password, setPassword] = useState<any>('')
    const [confirmCode, setConfirmCode] = useState<any>('')
    const [error, setError] = useState<any>('')
    const [errorCode, setErrorCode] = useState<any>('')
    const [codeSent, setCodeSent] = useState<any>(false)
    const [isSendingCode, setIsSendingCode] = useState<any>(false)
    const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null)
    const [isBlocked, setIsBlocked] = useState<any>(false)
    
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    
    const [login, { isLoading: isLoggingIn }] = useLoginMutation()
    const [getUserInfo] = useLazyUserInfoQuery()

    // useRef для полной блокировки
    const isSendingCodeRef = useRef(false)

    // Редирект если уже авторизован
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/platform/unregistered')
        }
    }, [isAuthenticated, navigate])

    const handleSendCode = async () => {
        if (isSendingCodeRef.current) {
            //('SendCode уже выполняется, пропускаем')
            return
        }

        setError('')
        setErrorCode('')
        setRemainingAttempts(null)
        setIsBlocked(false)
        setIsSendingCode(true)
        isSendingCodeRef.current = true

        const data: PostSendCodeRequest = {
            username: email,
            password: password
        }
        
        try {
            //('Выполняем sendCode запрос через fetch')
            
            const response = await fetch(`${API_URL}/auth/send_code/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(data),
            })

            const responseData = await response.json()
            
            //('SendCode response:', responseData);

            if (!response.ok) {
                // Обработка ошибки блокировки аккаунта
                if (responseData.error && responseData.error.includes('Ваш аккаунт временно заблокирован')) {
                    setError(responseData.error)
                    setIsBlocked(true)
                    return
                }
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Проверяем успешный ответ
            if (responseData.message === 'Код верификации отправлен' || responseData.access_token) {
                //('SendCode успешен');
                
                if (responseData.access_token) {
                    //('Токены получены в sendCode, сохраняем...');

                    dispatch(setTokens({
                        accessToken: responseData.access_token,
                        refreshToken: responseData.refresh_token || ''
                    }))

                    try {
                        //('Делаем запрос userInfo после sendCode...');
                        const userInfoResponse = await getUserInfo().unwrap()
                        dispatch(setUserInfo(userInfoResponse))
                        //('UserInfo получен и сохранен после sendCode:', userInfoResponse);
                        
                        // Если получили userInfo с токенами, сразу редиректим
                        navigate('/platform/unregistered')
                    } catch (userInfoError) {
                        console.error('Ошибка получения userInfo после sendCode:', userInfoError)
                        localStorage.setItem('loginEmail', email)
                        setCodeSent(true)
                    }
                } else {
                    localStorage.setItem('loginEmail', email)
                    //('Email сохранен в localStorage:', email)
                    setCodeSent(true)
                }
            } else {
                setError('Неверный логин или пароль')
                console.error('Неожиданный ответ от sendCode:', responseData)
            }
            
        } catch (err: any) {
            console.error('Ошибка отправки кода:', err)
            setError('Ошибка отправки кода. Попробуйте еще раз.')
        } finally {
            setIsSendingCode(false)
            isSendingCodeRef.current = false
        }
    }

    const handleLogin = async () => {
        //('Выполняется handleLogin')
        setErrorCode('')
        setRemainingAttempts(null)

        const data: PostLoginRequest = {
            username: email,
            password: password,
            code: confirmCode
        }
        
        try {
            const result = await login(data).unwrap()
            
            if (result.access_token) {
                //('Получены токены от login');

                dispatch(setTokens({
                    accessToken: result.access_token,
                    refreshToken: result.refresh_token || ''
                }))

                try {
                    //('Делаем запрос userInfo после login...');
                    const userInfoResponse = await getUserInfo().unwrap()
                    dispatch(setUserInfo(userInfoResponse))
                    //('UserInfo сохранен в Redux и localStorage');
                    localStorage.removeItem('loginEmail')
                    setCodeSent(false)
                    setConfirmCode('')
                    setRemainingAttempts(null)
                    setIsBlocked(false)
                    navigate('/platform/unregistered')
                } catch (userInfoError) {
                    console.error('Ошибка получения данных пользователя после login:', userInfoError)
                    navigate('/platform/unregistered')
                }
            } else {
                console.error('access_token не пришел в ответе login');
                setErrorCode('Неверный код доступа')
            }
        } catch (err: any) {
            console.error('Ошибка входа:', err)
            
            // Обработка ошибок с количеством попыток
            if (err.data && err.data.non_field_errors) {
                const errorMessages = err.data.non_field_errors;
                
                // Проверяем сообщение о превышении попыток
                const blockedMessage = errorMessages.find((msg: string) => 
                    msg.includes('Превышено количество попыток ввода кода')
                );
                
                if (blockedMessage) {
                    setErrorCode(blockedMessage);
                    setIsBlocked(true);
                    return;
                }
                
                // Проверяем сообщение о неверном коде с количеством оставшихся попыток
                const attemptMessage = errorMessages.find((msg: string) => 
                    msg.includes('Неверный код верификации')
                );
                
                if (attemptMessage) {
                    // Извлекаем количество оставшихся попыток из сообщения
                    const attemptsMatch = attemptMessage.match(/Осталось попыток: (\d+)/);
                    if (attemptsMatch) {
                        const attempts = parseInt(attemptsMatch[1]);
                        setRemainingAttempts(attempts);
                        setErrorCode(attemptMessage);
                        
                        // Блокируем если осталось 0 попыток
                        if (attempts === 0) {
                            setIsBlocked(true);
                        }
                    } else {
                        setErrorCode(attemptMessage);
                    }
                } else {
                    setErrorCode(errorMessages[0] || 'Неверный код доступа');
                }
            } else {
                setErrorCode('Неверный код доступа');
            }
        }
    }

    // Загрузка сохраненного email при монтировании компонента
    useEffect(() => {
        const savedEmail = localStorage.getItem('loginEmail')
        if (savedEmail) {
            setEmail(savedEmail)
            //('Email восстановлен из localStorage:', savedEmail)
        }
    }, [])

    const isLoading = isSendingCode || isLoggingIn

    // Блокируем кнопку если аккаунт заблокирован или осталось 0 попыток
    const isLoginButtonDisabled = isLoggingIn || !confirmCode || isBlocked || remainingAttempts === 0
    const isSendCodeButtonDisabled = isSendingCode || !email || !password || isBlocked

    if (isAuthenticated) {
        return (
            <div className={classes.container}>
                <div className={classes.login}>
                    <Text>Перенаправление...</Text>
                </div>
            </div>
        )
    }

    return (
        <div className={classes.container}>
            <div className={classes.login}>
                <Text style={{ color: '#0078d2' }} weight='bold' size='2xl'>Геоиндикатор</Text>
                
                <TextField
                    label='Электронная почта'
                    value={email}
                    type='text'
                    placeholder='example@mail.ru'
                    onChange={setEmail}
                    disabled={isLoading || isBlocked}
                />
                
                <TextField
                    label='Пароль'
                    value={password}
                    type='password'
                    placeholder='Введите пароль'
                    onChange={setPassword}
                    disabled={isLoading || isBlocked}
                />
                
                {error && 
                    <div className={classes.error}>
                        <Text>{error}</Text>
                    </div>
                }
                
                {codeSent && (
                    <div style={{ width: '100%' }}>
                        <TextField
                            label='Код подтверждения'
                            value={confirmCode}
                            type='text'
                            style={{ paddingBottom: '20px' }}
                            placeholder='Введите код из письма'
                            onChange={setConfirmCode}
                            disabled={isLoading || isBlocked}
                        />
                        
                        <Button 
                            label={isLoggingIn ? 'Вход...' : 'Войти'} 
                            style={{ width: '100%' }} 
                            onClick={handleLogin}
                            disabled={isLoginButtonDisabled}
                        />
                    </div>
                )}
                
                {errorCode && 
                    <div className={classes.error}>
                        <Text>{errorCode}</Text>
                    </div>
                }
                
                {!codeSent && (
                    <Button 
                        label={isSendingCode ? 'Отправка кода...' : 'Продолжить'} 
                        style={{ width: '100%' }} 
                        onClick={handleSendCode}
                        disabled={isSendCodeButtonDisabled}
                    />
                )}
                
                <Text size='xs' view='secondary'>© {new Date().getFullYear()} ООО «Геоиндикатор»</Text>
            </div>
        </div>
    );
};

export default Login

// import { useState, useEffect, useRef } from 'react'
// import { Text } from '@consta/uikit/Text'
// import { TextField } from '@consta/uikit/TextField'
// import { Button } from '@consta/uikit/Button';
// import { useNavigate } from 'react-router-dom';
// import { useDispatch, useSelector } from 'react-redux';
// import { useLoginMutation } from '../../services/authService';
// import { PostLoginRequest, PostSendCodeRequest } from '../../services/types/TLoginServices';
// import { RootState } from '../../store/store';
// import { setTokens, setUserInfo } from '../../store/authSlice';
// import classes from './Login.module.scss'
// import { API_URL, LS_KEYS } from '../../store/constants';

// const Login = () => {
//     const [email, setEmail] = useState('')
//     const [password, setPassword] = useState('')
//     const [confirmCode, setConfirmCode] = useState('')
//     const [error, setError] = useState('')
//     const [errorCode, setErrorCode] = useState('')
//     const [codeSent, setCodeSent] = useState(false)
//     const [isSendingCode, setIsSendingCode] = useState(false)
//     const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null)
//     const [isBlocked, setIsBlocked] = useState(false)
    
//     const navigate = useNavigate()
//     const dispatch = useDispatch()
//     const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
//     const userInfo = useSelector((state: RootState) => state.auth.userInfo);
    
//     const [login, { isLoading: isLoggingIn }] = useLoginMutation()
//     const isSendingCodeRef = useRef(false)

//     // Редирект если уже авторизован
//     useEffect(() => {
//         console.log('Проверка авторизации в Login:', { isAuthenticated, userInfo });
        
//         if (isAuthenticated && userInfo) {
//             console.log('Пользователь авторизован, перенаправление...');
//             if (!userInfo.is_registered) {
//                 navigate('/platform/unregistered');
//             } else {
//                 navigate('/platform/');
//             }
//         }
//     }, [isAuthenticated, userInfo, navigate]);

//     // Функция для получения userInfo через fetch
//     const fetchUserInfo = async (accessToken: string) => {
//         try {
//             console.log('Получаем userInfo через fetch с токеном:', accessToken.substring(0, 20) + '...');
            
//             const response = await fetch(`/user/user_info/`, {
//   method: 'GET',
//   headers: {
//     'Authorization': `Bearer ${accessToken}`,
//     'Content-Type': 'application/json',
//     'Accept': 'application/json',
//   },
// });
            
//             if (!response.ok) {
//                 throw new Error(`HTTP ошибка! статус: ${response.status}`);
//             }
            
//             const userInfoResponse = await response.json();
//             console.log('UserInfo получен через fetch:', userInfoResponse);
//             return userInfoResponse;
//         } catch (error) {
//             console.error('Ошибка получения userInfo через fetch:', error);
//             throw error;
//         }
//     };

//     const handleSendCode = async () => {
//         if (isSendingCodeRef.current) {
//             console.log('SendCode уже выполняется, пропускаем');
//             return;
//         }

//         setError('');
//         setErrorCode('');
//         setRemainingAttempts(null);
//         setIsBlocked(false);
//         setIsSendingCode(true);
//         isSendingCodeRef.current = true;

//         const data: PostSendCodeRequest = {
//             username: email,
//             password: password
//         };
        
//         try {
//             console.log('Отправка кода на', API_URL);
            
//             const response = await fetch(`/auth/send_code/`, {
//   method: 'POST',
//   headers: {
//     'Content-Type': 'application/json',
//     'Accept': 'application/json',
//   },
//   body: JSON.stringify(data),
// });

//             const responseData = await response.json();
            
//             console.log('SendCode ответ:', responseData);

//             if (!response.ok) {
//                 if (responseData.error && responseData.error.includes('Ваш аккаунт временно заблокирован')) {
//                     setError(responseData.error);
//                     setIsBlocked(true);
//                     return;
//                 }
//                 throw new Error(`HTTP ошибка! статус: ${response.status}`);
//             }

//             // Проверяем успешный ответ
//             if (responseData.message === 'Код верификации отправлен' || responseData.access_token) {
//                 console.log('SendCode успешен');
                
//                 if (responseData.access_token) {
//                     console.log('Токены получены, сохраняем...');

//                     // Сохраняем токены
//                     localStorage.setItem(LS_KEYS.token, responseData.access_token);
//                     if (responseData.refresh_token) {
//                         localStorage.setItem(LS_KEYS.refreshToken, responseData.refresh_token);
//                     }
                    
//                     // Диспатчим в Redux
//                     dispatch(setTokens({
//                         accessToken: responseData.access_token,
//                         refreshToken: responseData.refresh_token || ''
//                     }));

//                     // Пробуем получить userInfo
//                     try {
//                         const userInfoResponse = await fetchUserInfo(responseData.access_token);
                        
//                         // Сохраняем userInfo
//                         dispatch(setUserInfo(userInfoResponse));
//                         console.log('UserInfo сохранен в Redux и localStorage');
                        
//                         // Проверяем is_registered
//                         if (!userInfoResponse.is_registered) {
//                             navigate('/platform/unregistered');
//                         } else {
//                             navigate('/platform/');
//                         }
//                     } catch (userInfoError) {
//                         console.error('Ошибка получения userInfo:', userInfoError);
//                         // Если не получили userInfo, но есть токены - все равно сохраняем email
//                         localStorage.setItem('loginEmail', email);
//                         setCodeSent(true);
//                     }
//                 } else {
//                     localStorage.setItem('loginEmail', email);
//                     console.log('Email сохранен, ожидаем код подтверждения');
//                     setCodeSent(true);
//                 }
//             } else {
//                 setError('Неверный логин или пароль');
//             }
            
//         } catch (err: any) {
//             console.error('Ошибка отправки кода:', err);
//             setError('Ошибка отправки кода. Попробуйте еще раз.');
//         } finally {
//             setIsSendingCode(false);
//             isSendingCodeRef.current = false;
//         }
//     }

//     const handleLogin = async () => {
//         console.log('Выполняется вход с кодом');
//         setErrorCode('');
//         setRemainingAttempts(null);

//         const data: PostLoginRequest = {
//             username: email,
//             password: password,
//             code: confirmCode
//         };
        
//         try {
//             const result = await login(data).unwrap();
//             console.log('Результат входа:', result);
            
//             if (result.access_token) {
//                 console.log('Токены получены при входе');

//                 // Сохраняем токены
//                 localStorage.setItem(LS_KEYS.token, result.access_token);
//                 if (result.refresh_token) {
//                     localStorage.setItem(LS_KEYS.refreshToken, result.refresh_token);
//                 }
                
//                 // Диспатчим в Redux
//                 dispatch(setTokens({
//                     accessToken: result.access_token,
//                     refreshToken: result.refresh_token || ''
//                 }));

//                 // Пробуем получить userInfo
//                 try {
//                     const userInfoResponse = await fetchUserInfo(result.access_token);
                    
//                     // Сохраняем userInfo
//                     dispatch(setUserInfo(userInfoResponse));
//                     console.log('UserInfo сохранен');
                    
//                     localStorage.removeItem('loginEmail');
//                     setCodeSent(false);
//                     setConfirmCode('');
//                     setRemainingAttempts(null);
//                     setIsBlocked(false);
                    
//                     if (!userInfoResponse.is_registered) {
//                         navigate('/platform/unregistered');
//                     } else {
//                         navigate('/platform/');
//                     }
//                 } catch (userInfoError) {
//                     console.error('Ошибка получения данных пользователя:', userInfoError);
//                     // Даже если не получили userInfo, но есть токены - перенаправляем
//                     navigate('/platform/unregistered');
//                 }
//             } else {
//                 console.error('Токен не пришел в ответе');
//                 setErrorCode('Неверный код доступа');
//             }
//         } catch (err: any) {
//             console.error('Ошибка входа:', err);
            
//             // Обработка ошибок с количеством попыток
//             if (err.data && err.data.non_field_errors) {
//                 const errorMessages = err.data.non_field_errors;
                
//                 const blockedMessage = errorMessages.find((msg: string) => 
//                     msg.includes('Превышено количество попыток ввода кода')
//                 );
                
//                 if (blockedMessage) {
//                     setErrorCode(blockedMessage);
//                     setIsBlocked(true);
//                     return;
//                 }
                
//                 const attemptMessage = errorMessages.find((msg: string) => 
//                     msg.includes('Неверный код верификации')
//                 );
                
//                 if (attemptMessage) {
//                     const attemptsMatch = attemptMessage.match(/Осталось попыток: (\d+)/);
//                     if (attemptsMatch) {
//                         const attempts = parseInt(attemptsMatch[1]);
//                         setRemainingAttempts(attempts);
//                         setErrorCode(attemptMessage);
                        
//                         if (attempts === 0) {
//                             setIsBlocked(true);
//                         }
//                     } else {
//                         setErrorCode(attemptMessage);
//                     }
//                 } else {
//                     setErrorCode(errorMessages[0] || 'Неверный код доступа');
//                 }
//             } else {
//                 setErrorCode('Неверный код доступа');
//             }
//         }
//     }

//     // Загрузка сохраненного email при монтировании компонента
//     useEffect(() => {
//         const savedEmail = localStorage.getItem('loginEmail');
//         if (savedEmail) {
//             setEmail(savedEmail);
//             console.log('Email восстановлен из localStorage:', savedEmail);
//         }
//     }, []);

//     const isLoading = isSendingCode || isLoggingIn;
//     const isLoginButtonDisabled = isLoggingIn || !confirmCode || isBlocked || remainingAttempts === 0;
//     const isSendCodeButtonDisabled = isSendingCode || !email || !password || isBlocked;

//     if (isAuthenticated) {
//         return (
//             <div className={classes.container}>
//                 <div className={classes.login}>
//                     <Text>Перенаправление...</Text>
//                 </div>
//             </div>
//         );
//     }

//     return (
//         <div className={classes.container}>
//             <div className={classes.login}>
//                 <Text style={{ color: '#0078d2' }} weight='bold' size='2xl'>Геоиндикатор</Text>
                
//                 <TextField
//                     label='Электронная почта'
//                     value={email}
//                     type='text'
//                     placeholder='example@mail.ru'
//                     //@ts-ignore
//                     onChange={setEmail}
//                     disabled={isLoading || isBlocked}
//                 />
                
//                 <TextField
//                     label='Пароль'
//                     value={password}
//                     type='password'
//                     placeholder='Введите пароль'
//                      //@ts-ignore
//                     onChange={setPassword}
//                     disabled={isLoading || isBlocked}
//                 />
                
//                 {error && 
//                     <div className={classes.error}>
//                         <Text>{error}</Text>
//                     </div>
//                 }
                
//                 {codeSent && (
//                     <div style={{ width: '100%' }}>
//                         <TextField
//                             label='Код подтверждения'
//                             value={confirmCode}
//                             type='text'
//                             style={{ paddingBottom: '20px' }}
//                             placeholder='Введите код из письма'
//                              //@ts-ignore
//                             onChange={setConfirmCode}
//                             disabled={isLoading || isBlocked}
//                         />
                        
//                         <Button 
//                             label={isLoggingIn ? 'Вход...' : 'Войти'} 
//                             style={{ width: '100%' }} 
//                             onClick={handleLogin}
//                             disabled={isLoginButtonDisabled}
//                         />
//                     </div>
//                 )}
                
//                 {errorCode && 
//                     <div className={classes.error}>
//                         <Text>{errorCode}</Text>
//                     </div>
//                 }
                
//                 {!codeSent && (
//                     <Button 
//                         label={isSendingCode ? 'Отправка кода...' : 'Продолжить'} 
//                         style={{ width: '100%' }} 
//                         onClick={handleSendCode}
//                         disabled={isSendCodeButtonDisabled}
//                     />
//                 )}
                
//                 <Text size='xs' view='secondary'>© {new Date().getFullYear()} ООО «Геоиндикатор»</Text>
//             </div>
//         </div>
//     );
// };

// export default Login;